import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, CheckCircle2, Delete, Loader2, MapPin } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import {
  TIMECLOCK_PUNCH_KIND_VALUES,
  type TimeclockDeviceMeResponse,
  type TimeclockPunchKind,
} from '@aeris/contracts'

import {
  DeviceError,
  DeviceOfflineError,
  PUNCH_LABELS,
  fetchDeviceMe,
  fetchDeviceMemberPhoto,
  forgetDeviceToken,
  pairFromLocation,
  sendPunch,
} from '../lib/ponto-device.ts'

/**
 * Bater o ponto (F339, ADR-0050). PÚBLICA: quem abre é o tablet da loja ou o
 * celular do funcionário, pareado por um link do gerente.
 *
 * Na loja aparece a grade de quem bate ali: a pessoa toca no nome, digita o
 * código e a câmera tira a foto. No celular é direto o código, a foto e a
 * localização. A hora é sempre a do servidor.
 */

type Member = TimeclockDeviceMeResponse['members'][number]

type Done = {
  readonly name: string
  readonly kind: TimeclockPunchKind
  readonly at: string
  readonly flag: string
}

const DONE_MS = 4_000
/** O painel parado volta sozinho depois disso. */
const IDLE_MS = 60_000

const hhmm = (iso: string | Date): string =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  }).format(typeof iso === 'string' ? new Date(iso) : iso)

const initials = (name: string): string =>
  name
    .split(/\s+/u)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

const Shell = ({ children }: { children: ReactNode }): ReactNode => (
  <main className="flex min-h-dvh flex-col bg-[#0f172a] px-4 py-6 text-white">
    {children}
  </main>
)

const Clock = (): ReactNode => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date())
    }, 10_000)
    return () => {
      clearInterval(timer)
    }
  }, [])
  return (
    <p className="text-4xl font-bold tabular-nums" aria-label="Hora agora">
      {hhmm(now)}
    </p>
  )
}

const Notice = ({ children }: { children: ReactNode }): ReactNode => (
  <Shell>
    <div className="m-auto flex max-w-md flex-col items-center gap-3 text-center">
      {children}
    </div>
  </Shell>
)

/**
 * A câmera frontal ao vivo; sem permissão, cai para o arquivo da câmera.
 *
 * `enabled` falso não abre a câmera: no celular a página fica aberta o dia
 * todo, e a câmera só liga quando a pessoa começa a bater.
 */
const useCamera = (enabled: boolean) => {
  const video = useRef<HTMLVideoElement | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  // Sem `mediaDevices` (página fora de https, navegador antigo) a câmera ao
  // vivo não existe: vai direto para o arquivo da câmera.
  const [failed, setFailed] = useState(() => !('mediaDevices' in navigator))

  useEffect(() => {
    if (failed || !enabled) return
    let alive = true
    let opened: MediaStream | null = null
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((media) => {
        if (!alive) {
          media.getTracks().forEach((track) => {
            track.stop()
          })
          return
        }
        opened = media
        setStream(media)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
      opened?.getTracks().forEach((track) => {
        track.stop()
      })
      setStream(null)
    }
  }, [failed, enabled])

  useEffect(() => {
    if (video.current !== null && stream !== null) {
      video.current.srcObject = stream
      void video.current.play().catch(() => undefined)
    }
  }, [stream])

  const capture = useCallback(async (): Promise<Blob | null> => {
    const element = video.current
    if (element === null || element.videoWidth === 0) return null
    const canvas = document.createElement('canvas')
    const scale = Math.min(1, 960 / element.videoWidth)
    canvas.width = Math.round(element.videoWidth * scale)
    canvas.height = Math.round(element.videoHeight * scale)
    canvas
      .getContext('2d')
      ?.drawImage(element, 0, 0, canvas.width, canvas.height)
    return await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    })
  }, [])

  return { video, ready: stream !== null, failed, capture }
}

/**
 * A foto do arquivo da câmera vem no tamanho do sensor (às vezes passa dos 10
 * MB do envio): reduz pelo mesmo tamanho da captura ao vivo. Se o navegador
 * não decodifica, vai a original.
 */
const shrink = async (file: Blob): Promise<Blob> => {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, 960 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    canvas
      .getContext('2d')
      ?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    return await new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob ?? file)
        },
        'image/jpeg',
        0.85
      )
    })
  } catch {
    return file
  }
}

const where = async (): Promise<{
  latitude: number
  longitude: number
  accuracy: number
} | null> =>
  await new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        })
      },
      () => {
        resolve(null)
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 }
    )
  })

const PunchPanel = ({
  token,
  member,
  asksLocation,
  cameraOnDemand,
  onCancel,
  onDone,
  onRevoked,
}: {
  token: string
  member: Member
  asksLocation: boolean
  /** No celular a câmera só liga no primeiro dígito. */
  cameraOnDemand: boolean
  onCancel: (() => void) | null
  onDone: (done: Done) => void
  onRevoked: () => void
}): ReactNode => {
  const [started, setStarted] = useState(!cameraOnDemand)
  const {
    video,
    failed: cameraFailed,
    ready: cameraReady,
    capture,
  } = useCamera(started)
  const [pin, setPin] = useState('')
  const [kind, setKind] = useState<TimeclockPunchKind>(member.next)
  const [file, setFile] = useState<Blob | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      const photo = cameraFailed
        ? file === null
          ? null
          : await shrink(file)
        : await capture()
      if (photo === null) {
        setError('A foto não saiu. Olhe para a câmera e tente de novo.')
        return
      }
      const location = asksLocation ? await where() : null
      const result = await sendPunch(token, {
        employeeId: member.employeeId,
        pin,
        // Só manda o tipo quando a pessoa trocou o sugerido: senão o
        // servidor deduz, com a hora dele (revisão da F339, M2).
        kind: kind === member.next ? null : kind,
        location,
        photo,
      })
      onDone({
        name: result.punch.employeeName,
        kind: result.punch.kind,
        at: result.punch.punchedAt,
        flag: result.punch.flag,
      })
    } catch (cause) {
      setPin('')
      if (cause instanceof DeviceError && cause.status === 401) {
        onRevoked()
        return
      }
      setError(
        cause instanceof DeviceError
          ? cause.message
          : cause instanceof DeviceOfflineError
            ? 'Sem internet. A batida não foi registrada, tente de novo.'
            : 'Não deu certo. Tente de novo.'
      )
    } finally {
      setBusy(false)
    }
  }

  const press = (digit: string) => {
    setError(null)
    setStarted(true)
    setPin((current) => (current.length >= 6 ? current : current + digit))
  }

  const ready =
    !busy && pin.length >= 4 && (cameraFailed ? file !== null : cameraReady)

  // O teclado físico também vale (o PC da loja): números, apagar e Enter.
  const onKey = useRef<(event: KeyboardEvent) => void>(() => undefined)
  useEffect(() => {
    onKey.current = (event) => {
      if (busy) return
      if (/^\d$/u.test(event.key)) press(event.key)
      else if (event.key === 'Backspace') setPin((c) => c.slice(0, -1))
      else if (event.key === 'Enter' && ready) void confirm()
    }
  })
  // Parado um minuto: no tablet volta pra grade (a próxima pessoa não herda
  // o código pela metade nem a câmera); no celular apaga o código e desliga a
  // câmera.
  const onIdle = useRef<() => void>(() => undefined)
  useEffect(() => {
    onIdle.current = () => {
      if (onCancel !== null) {
        onCancel()
        return
      }
      setPin('')
      setFile(null)
      setStarted(false)
    }
  })
  const idle = onCancel === null && !started && pin === ''
  useEffect(() => {
    if (busy || idle) return
    const timer = setTimeout(() => {
      onIdle.current()
    }, IDLE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [busy, idle, pin, kind, file, error])

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey.current(event)
    }
    window.addEventListener('keydown', listener)
    return () => {
      window.removeEventListener('keydown', listener)
    }
  }, [])

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center gap-4">
      <p className="text-2xl font-semibold">{member.name}</p>

      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-black">
        {cameraFailed ? (
          <label className="flex h-full cursor-pointer flex-col items-center justify-center gap-2 text-center text-sm text-[#cbd5e1]">
            <Camera aria-hidden="true" className="h-8 w-8" />
            {file === null
              ? 'Toque para tirar a foto'
              : 'Foto pronta — toque para tirar outra'}
            <input
              type="file"
              accept="image/*"
              capture="user"
              className="sr-only"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
              }}
            />
          </label>
        ) : !started ? (
          <p className="flex h-full items-center justify-center px-6 text-center text-sm text-[#cbd5e1]">
            A câmera liga quando você começar a digitar o código.
          </p>
        ) : (
          <video
            ref={video}
            muted
            playsInline
            aria-label="Câmera"
            className="h-full w-full -scale-x-100 object-cover"
          />
        )}
      </div>

      <div
        className="flex flex-wrap justify-center gap-2"
        role="radiogroup"
        aria-label="Tipo da batida"
        onKeyDown={(event) => {
          const step =
            event.key === 'ArrowRight' || event.key === 'ArrowDown'
              ? 1
              : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                ? -1
                : 0
          if (step === 0) return
          event.preventDefault()
          const all = TIMECLOCK_PUNCH_KIND_VALUES
          const at = all.indexOf(kind)
          const next = all[(at + step + all.length) % all.length] ?? kind
          setKind(next)
          const buttons =
            event.currentTarget.querySelectorAll<HTMLButtonElement>(
              '[role="radio"]'
            )
          buttons[all.indexOf(next)]?.focus()
        }}
      >
        {TIMECLOCK_PUNCH_KIND_VALUES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            tabIndex={kind === option ? 0 : -1}
            onClick={() => {
              setKind(option)
            }}
            className={`rounded-full px-3 py-1.5 text-sm ${kind === option ? 'bg-[#22c55e] font-semibold text-[#052e16]' : 'bg-[#1e293b] text-[#cbd5e1]'}`}
          >
            {PUNCH_LABELS[option]}
          </button>
        ))}
      </div>

      <div className="flex h-10 items-center gap-3">
        <span role="status" aria-live="polite" className="sr-only">
          {`Código: ${String(pin.length)} números`}
        </span>
        {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
          <span
            key={i}
            className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-white' : 'border border-[#475569]'}`}
          />
        ))}
      </div>

      <div className="grid w-full max-w-xs grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button
            key={digit}
            type="button"
            disabled={busy}
            onClick={() => {
              press(digit)
            }}
            className="h-16 rounded-xl bg-[#1e293b] text-2xl font-semibold active:bg-[#334155]"
          >
            {digit}
          </button>
        ))}
        <button
          type="button"
          disabled={busy}
          aria-label="Apagar"
          onClick={() => {
            setPin((current) => current.slice(0, -1))
          }}
          className="flex h-16 items-center justify-center rounded-xl bg-[#1e293b]"
        >
          <Delete aria-hidden="true" className="h-6 w-6" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            press('0')
          }}
          className="h-16 rounded-xl bg-[#1e293b] text-2xl font-semibold active:bg-[#334155]"
        >
          0
        </button>
        <button
          type="button"
          disabled={!ready}
          onClick={() => {
            void confirm()
          }}
          className="h-16 rounded-xl bg-[#22c55e] text-lg font-bold text-[#052e16] disabled:opacity-40"
        >
          {busy ? (
            <Loader2
              aria-label="Enviando"
              className="mx-auto h-6 w-6 animate-spin"
            />
          ) : (
            'OK'
          )}
        </button>
      </div>

      {asksLocation ? (
        <p className="flex items-center gap-1 text-xs text-[#94a3b8]">
          <MapPin aria-hidden="true" className="h-3.5 w-3.5" />A batida pelo
          celular leva a sua localização.
        </p>
      ) : null}

      {error === null ? null : (
        <p
          role="alert"
          className="rounded-lg bg-[#7f1d1d] px-4 py-2 text-center text-sm"
        >
          {error}
        </p>
      )}

      {onCancel === null ? null : (
        <button
          type="button"
          onClick={onCancel}
          className="py-2 text-sm text-[#94a3b8] underline"
        >
          Voltar
        </button>
      )}
    </div>
  )
}

const MemberCard = ({
  token,
  member,
  onPick,
}: {
  token: string
  member: Member
  onPick: () => void
}): ReactNode => {
  const [photo, setPhoto] = useState<string | null>(null)
  // A URL é do cache (`ponto-device.ts`): quem revoga é ele, quando a foto
  // muda ou o aparelho é despareado — não a montagem do cartão.
  useEffect(() => {
    if (member.photoKey === null) return
    let alive = true
    void fetchDeviceMemberPhoto(token, member.employeeId, member.photoKey).then(
      (loaded) => {
        if (alive) setPhoto(loaded)
      }
    )
    return () => {
      alive = false
    }
  }, [token, member.employeeId, member.photoKey])

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={member.locked}
      className="flex flex-col items-center gap-2 rounded-2xl bg-[#1e293b] p-4 text-center active:bg-[#334155] disabled:opacity-50"
    >
      {photo === null || member.photoKey === null ? (
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#334155] text-2xl font-semibold">
          {initials(member.name)}
        </span>
      ) : (
        <img
          src={photo}
          alt=""
          className="h-20 w-20 rounded-full object-cover"
        />
      )}
      <span className="text-base font-semibold">{member.name}</span>
      <span className="text-xs text-[#94a3b8]">
        {member.locked
          ? 'Código travado — chame o gerente'
          : PUNCH_LABELS[member.next]}
      </span>
    </button>
  )
}

export const BaterPontoPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const [token, setToken] = useState<string | null>(() => pairFromLocation())
  const [picked, setPicked] = useState<string | null>(null)
  const [done, setDone] = useState<Done | null>(null)

  const me = useQuery({
    queryKey: ['ponto-aparelho', token],
    queryFn: async ({ signal }) => {
      try {
        return await fetchDeviceMe(token ?? '', signal)
      } catch (error) {
        // O gerente revogou: o token salvo não serve mais para nada.
        if (error instanceof DeviceError && error.status === 401) {
          forgetDeviceToken()
        }
        throw error
      }
    },
    enabled: token !== null,
    retry: (count, error) => !(error instanceof DeviceError) && count < 3,
    refetchInterval: 60_000,
  })

  const revoked = useCallback(() => {
    forgetDeviceToken()
    setToken(null)
  }, [])

  useEffect(() => {
    if (done === null) return
    const timer = setTimeout(() => {
      setDone(null)
    }, DONE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [done])

  const unpaired =
    token === null ||
    (me.error instanceof DeviceError && me.error.status === 401)

  if (unpaired) {
    return (
      <Notice>
        <p className="text-xl font-semibold">Este aparelho não está liberado</p>
        <p className="text-sm text-[#94a3b8]">
          Peça ao gerente o link em Ponto → Aparelhos e abra neste aparelho.
        </p>
      </Notice>
    )
  }

  // Uma recarga que falha com a lista já na tela não derruba o painel: quem
  // está digitando o código continua, e o aviso fica no cabeçalho.
  if (me.data === undefined) {
    if (me.isError) {
      return (
        <Notice>
          <p className="text-lg font-semibold">
            {me.error instanceof DeviceError
              ? me.error.message
              : 'Sem internet agora.'}
          </p>
          <button
            type="button"
            onClick={() => {
              void me.refetch()
            }}
            className="rounded-xl bg-[#1e293b] px-6 py-3"
          >
            Tentar de novo
          </button>
        </Notice>
      )
    }
    return (
      <Notice>
        <Loader2
          aria-label="Carregando"
          className="h-8 w-8 animate-spin text-[#94a3b8]"
        />
      </Notice>
    )
  }

  const data = me.data
  const finish = (result: Done) => {
    setPicked(null)
    setDone(result)
    void queryClient.invalidateQueries({ queryKey: ['ponto-aparelho', token] })
  }

  if (done !== null) {
    return (
      <Notice>
        <CheckCircle2 aria-hidden="true" className="h-20 w-20 text-[#22c55e]" />
        <p role="status" className="text-2xl font-bold">
          {done.name}
        </p>
        <p className="text-xl">
          {PUNCH_LABELS[done.kind]} às {hhmm(done.at)}
        </p>
        {done.flag === 'fora_da_loja' ? (
          <p className="text-sm text-[#fbbf24]">Registrada longe da loja.</p>
        ) : done.flag === 'sem_localizacao' ? (
          <p className="text-sm text-[#fbbf24]">Registrada sem localização.</p>
        ) : null}
      </Notice>
    )
  }

  const isPhone = data.device.kind === 'celular'
  const pickedMember = isPhone
    ? (data.members[0] ?? null)
    : (data.members.find((member) => member.employeeId === picked) ?? null)

  return (
    <Shell>
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-[#94a3b8]">{data.device.branchName}</p>
          <p className="text-lg font-semibold">Bater o ponto</p>
          {me.isError ? (
            <p role="status" className="text-sm text-[#fbbf24]">
              Sem internet agora: a lista pode estar velha.
            </p>
          ) : null}
        </div>
        <Clock />
      </header>

      {pickedMember !== null ? (
        pickedMember.locked ? (
          <p className="m-auto text-center text-lg">
            Código travado. Peça ao gerente para destravar.
          </p>
        ) : (
          <PunchPanel
            // O tipo sugerido mudou (bateu em outro aparelho): o painel
            // recomeça com o novo, em vez de guardar o velho.
            key={`${pickedMember.employeeId}:${pickedMember.next}`}
            token={token}
            member={pickedMember}
            asksLocation={data.asksLocation}
            cameraOnDemand={isPhone}
            onCancel={
              isPhone
                ? null
                : () => {
                    setPicked(null)
                  }
            }
            onDone={finish}
            onRevoked={revoked}
          />
        )
      ) : data.members.length === 0 ? (
        <p className="m-auto max-w-md text-center text-[#94a3b8]">
          {isPhone
            ? 'Você não está liberado para bater o ponto pelo celular. Fale com o gerente.'
            : 'Ninguém desta loja bate o ponto aqui ainda. O gerente cadastra em Ponto → Funcionários.'}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {data.members.map((member) => (
            <MemberCard
              key={member.employeeId}
              token={token}
              member={member}
              onPick={() => {
                setPicked(member.employeeId)
              }}
            />
          ))}
        </div>
      )}
    </Shell>
  )
}
